import { QueryClient, useMutation, useQuery } from '@tanstack/react-query'
import axiosOriginal from 'axios'

import storage from 'global-utils/Storage'
import System from 'system'

import type { QueryKey } from '@tanstack/query-core'
import type { InvalidateQueryFilters } from '@tanstack/react-query'
import type { DefinedInitialDataOptions, UndefinedInitialDataOptions } from '@tanstack/react-query/src/queryOptions'
import type { UseMutationOptions, UseMutationResult, UseQueryOptions } from '@tanstack/react-query/src/types'
import type { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios'

export type DefaultError = AxiosError<{ error?: { message: string } }>

interface Credentials {
  accessToken: string
  expiresIn: number
  refreshToken: string
  typeToken: string
}

export const axiosPublic = axiosOriginal.create({
  baseURL: System.api_url,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Requested-With': 'RQ',
  },
  withCredentials: false,
})

export const axiosPrivate = axiosOriginal.create({
  baseURL: System.api_url,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Requested-With': 'RQ',
  },
  withCredentials: false,
})

export const refreshBearer = async () => {
  const { rt } = storage.get('auth.credentials') || {}

  if (rt) {
    const response = await axiosPublic.post<Credentials>(
      'seguridad/refresh',
      {
        client_id: System.env.client.id,
        client_secret: System.env.client.secret,
        grant_type: 'refresh_token',
        provider: 'MedSystem',
        refresh_token: rt,
        scope: 'user',
        token: rt,
      },
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        },
        withCredentials: false,
      },
    )

    if (response.data) {
      storage.set('auth.type', 'auth.credentials', true)
      storage.set(
        'auth.credentials',
        {
          at: response.data.accessToken,
          ex: response.data.expiresIn * 1000 + Date.now(),
          rt: response.data.refreshToken || null,
          tt: response.data.typeToken,
        },
        true,
      )

      Object.assign(System.AUTH_HEADERS, {
        Accept: 'application/json',
        Authorization: `${response.data.typeToken} ${response.data.accessToken}`,
      })

      return response.data
    }
  }
  return null
}

export const setAxiosBearer = () => {
  const requestIntercept = axiosPrivate.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      if (!System.AUTH_HEADERS.Authorization) {
        config.signal = AbortSignal.abort()
      } else if (!config.headers.Authorization) {
        config.headers.Authorization = System.AUTH_HEADERS.Authorization
      }
      return config
    },
    (error) => Promise.reject(error),
  )

  const responseIntercept = axiosPrivate.interceptors.response.use(
    (response) => response,
    async (error: DefaultError) => {
      const previousRequest = error?.config as { sent?: boolean } & typeof error.config
      if (previousRequest && error?.response?.status === 401 && !previousRequest.sent) {
        previousRequest.sent = true
        const newBearer = await refreshBearer()
        if (newBearer) {
          previousRequest.headers.Authorization = System.AUTH_HEADERS.Authorization
          return axiosPrivate(previousRequest)
        }
      }
      throw error
    },
  )

  return () => {
    axiosPrivate.interceptors.request.eject(requestIntercept)
    axiosPrivate.interceptors.response.eject(responseIntercept)
  }
}

export const axiosPrivateEject = setAxiosBearer()

export const queryClient = new QueryClient({
  defaultOptions: {
    mutations: {
      retry: 0,
    },
    queries: {
      retry: 2,
      staleTime: 60_000 * 60, // 1 min
    },
  },
})

type RequestBaseType =
  | {
      anonymous?: boolean
      body?: object | URLSearchParams
      formEncode?: boolean
      headers?: AxiosRequestConfig['headers']
      method: 'post'
      url: string
    }
  | {
      anonymous?: boolean
      body?: object | URLSearchParams
      formEncode?: boolean
      headers?: AxiosRequestConfig['headers']
      method: 'put'
      url: string
    }
  | {
      anonymous?: boolean
      headers?: AxiosRequestConfig['headers']
      method: 'delete'
      url: string
    }
  | {
      anonymous?: boolean
      headers?: AxiosRequestConfig['headers']
      method: 'get'
      url: string
    }
  | string

type RequestType = RequestBaseType | (() => RequestBaseType)

export interface ServerResponse<TData> {
  data?: null | TData
  error?: {
    code: number | string
    error: string
    message: string
    trace?: string
  }
  pagination?: {
    current_page: number
    from: number
    last_page: number
    next_page_url: string
    per_page: number
    prev_page_url: string
    to: number
    total: number
  }
}

type RequestMutateBaseType =
  | {
      formEncode?: boolean
      headers?: AxiosRequestConfig['headers']
      method: 'post' | 'put'
      url: string
    }
  | {
      headers?: AxiosRequestConfig['headers']
      method: 'delete'
      url: string
    }
  | string

type RequestMutateType<TVariables> = ((params: TVariables) => RequestMutateBaseType) | RequestMutateBaseType

export const useFetch = <
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  request: RequestType,
  options?:
    | DefinedInitialDataOptions<TQueryFnData, TError, TData, TQueryKey>
    | UndefinedInitialDataOptions<TQueryFnData, TError, TData, TQueryKey>
    | UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
) => {
  const newOptions:
    | DefinedInitialDataOptions<TQueryFnData, TError, TData, TQueryKey>
    | UndefinedInitialDataOptions<TQueryFnData, TError, TData, TQueryKey>
    | UseQueryOptions<TQueryFnData, TError, TData, TQueryKey> = {
    ...options,
    // @ts-expect-error url prop is not defined
    queryKey: options?.queryKey || [typeof request === 'string' ? request : request.url, 'get'],
  }

  // eslint-disable-next-line no-param-reassign,react-hooks/immutability
  request = typeof request === 'function' ? request() : request
  const axiosInstance =
    typeof request === 'object' && 'anonymous' in request && request.anonymous ? axiosPublic : axiosPrivate

  if (typeof request === 'string') {
    newOptions.queryFn = async ({ signal }) => {
      if (System.AUTH_HEADERS['x-shared-access']) {
        const headers = {
          'x-url': `${System.api_base_url}/${request}`,
        }
        const url = `general/consultaDocumento/?${request}`
        const response = await axiosInstance.get<TQueryFnData>(`${System.api_url}/${url}`, {
          headers,
          signal,
        })
        return response.data
      }
      const response = await axiosInstance.get<TQueryFnData>(request, { signal })
      return response.data
    }
  } else if (request.method === 'get') {
    newOptions.queryFn = async ({ signal }) => {
      if (System.AUTH_HEADERS['x-shared-access']) {
        const headers = {
          ...request.headers,
          'x-url': `${System.api_base_url}/${request}`,
        }
        const url = `general/consultaDocumento/?${request}`
        const response = await axiosInstance.get<TQueryFnData>(`${System.api_url}/${url}`, {
          headers,
          signal,
        })
        return response.data
      }

      const response = await axiosInstance.get<TQueryFnData>(request.url, {
        headers: request.headers,
        signal,
      })
      return response.data
    }
  } else if (request.method === 'put') {
    newOptions.queryFn = async ({ signal }) => {
      const response = await axiosInstance.put<TQueryFnData>(request.url, request.body, {
        headers: {
          ...request.headers,
          'Content-Type': request.formEncode ? 'application/x-www-form-urlencoded' : 'application/json',
        },
        signal,
      })
      return response.data
    }
  } else if (request.method === 'post') {
    newOptions.queryFn = async ({ signal }) => {
      const response = await axiosInstance.post<TQueryFnData>(request.url, request.body, {
        headers: {
          ...request.headers,
          'Content-Type': request.formEncode ? 'application/x-www-form-urlencoded' : 'application/json',
        },
        signal,
      })
      return response.data
    }
  }

  return useQuery<TQueryFnData, TError, TData, TQueryKey>(newOptions, queryClient)
}

export function useMutate<TVariables = void, TData = unknown, TError = DefaultError, TOnMutateResult = unknown>(
  request: RequestMutateType<TVariables>,
  options?: {
    invalidate: InvalidateQueryFilters['queryKey'] | InvalidateQueryFilters['queryKey'][]
  } & UseMutationOptions<TData, TError, TVariables, TOnMutateResult>,
): UseMutationResult<TData, TError, TVariables, TOnMutateResult> {
  const newOptions: UseMutationOptions<TData, TError, TVariables, TOnMutateResult> = {
    ...options,
    mutationKey: options?.mutationKey,
    onSuccess: async (data, variables, onMutateResult, context) => {
      let queryKeyList: InvalidateQueryFilters['queryKey'][] = []

      if (Array.isArray(options?.invalidate)) {
        if (Array.isArray(options.invalidate[0])) {
          queryKeyList = options.invalidate as InvalidateQueryFilters['queryKey'][]
        } else {
          queryKeyList.push(options.invalidate)
        }
      }

      if (queryKeyList.length > 0) {
        await Promise.all(queryKeyList.map((queryKey) => queryClient.invalidateQueries({ queryKey })) || [])
      }
      if (options?.onSuccess) {
        await options.onSuccess(data, variables, onMutateResult, context)
      }
    },
    retry: 0,
  }

  newOptions.mutationFn = async (mutationData: TVariables) => {
    const requestObj = typeof request === 'function' ? request(mutationData) : request

    const mergedRequest = {
      formEncode: typeof requestObj !== 'string' && 'formEncode' in requestObj ? requestObj.formEncode : false,
      headers: {
        ...(typeof requestObj !== 'string' && 'headers' in requestObj ? requestObj.headers : {}),
      },
      method: typeof requestObj === 'string' ? 'post' : requestObj.method,
      url: typeof requestObj === 'string' ? requestObj : requestObj.url,
    }

    if (mergedRequest.method === 'put') {
      const response = await axiosPrivate.put<TData>(mergedRequest.url, mutationData, {
        headers: {
          ...mergedRequest.headers,
          'Content-Type': mergedRequest.formEncode ? 'application/x-www-form-urlencoded' : 'application/json',
        },
      })
      return response.data
    }
    if (mergedRequest.method === 'delete') {
      const response = await axiosPrivate.delete<TData>(mergedRequest.url, {
        headers: {
          ...mergedRequest.headers,
          'Content-Type': 'application/json',
        },
      })
      return response.data
    }

    const response = await axiosPrivate.post<TData>(mergedRequest.url, mutationData, {
      headers: {
        ...mergedRequest.headers,
        'Content-Type': mergedRequest.formEncode ? 'application/x-www-form-urlencoded' : 'application/json',
      },
    })
    return response.data
  }

  return useMutation<TData, TError, TVariables, TOnMutateResult>(newOptions, queryClient)
}

export const useFetchActions = <
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  request: RequestType,
  options?:
    | DefinedInitialDataOptions<TQueryFnData, TError, TData, TQueryKey>
    | UndefinedInitialDataOptions<TQueryFnData, TError, TData, TQueryKey>
    | UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
) => {
  const newOptions:
    | DefinedInitialDataOptions<TQueryFnData, TError, TData, TQueryKey>
    | UndefinedInitialDataOptions<TQueryFnData, TError, TData, TQueryKey>
    | UseQueryOptions<TQueryFnData, TError, TData, TQueryKey> = {
    ...options,
    // @ts-expect-error url prop is not defined
    queryKey: options?.queryKey || [typeof request === 'string' ? request : request.url, 'get'],
  }

  // eslint-disable-next-line no-param-reassign,react-hooks/immutability
  request = typeof request === 'function' ? request() : request
  const axiosInstance =
    typeof request === 'object' && 'anonymous' in request && request.anonymous ? axiosPublic : axiosPrivate
  const baseURL =
    process.env.NODE_ENV === 'development' ? 'http://localhost:6065/api/' : 'https://actions.medsystem.co/api/'

  if (typeof request === 'string') {
    newOptions.queryFn = async ({ signal }) => {
      const response = await axiosInstance.get<TQueryFnData>(request, { baseURL, signal })
      return response.data
    }
  } else if (request.method === 'get') {
    newOptions.queryFn = async ({ signal }) => {
      const response = await axiosInstance.get<TQueryFnData>(request.url, {
        baseURL,
        headers: request.headers,
        signal,
      })
      return response.data
    }
  } else if (request.method === 'put') {
    newOptions.queryFn = async ({ signal }) => {
      const response = await axiosInstance.put<TQueryFnData>(request.url, request.body, {
        baseURL,
        headers: {
          ...request.headers,
          'Content-Type': request.formEncode ? 'application/x-www-form-urlencoded' : 'application/json',
        },
        signal,
      })
      return response.data
    }
  } else if (request.method === 'post') {
    newOptions.queryFn = async ({ signal }) => {
      const response = await axiosInstance.post<TQueryFnData>(request.url, request.body, {
        baseURL,
        headers: {
          ...request.headers,
          'Content-Type': request.formEncode ? 'application/x-www-form-urlencoded' : 'application/json',
        },
        signal,
      })
      return response.data
    }
  }

  return useQuery<TQueryFnData, TError, TData, TQueryKey>(newOptions, queryClient)
}

export function useMutateActions<TVariables = void, TData = unknown, TError = DefaultError, TOnMutateResult = unknown>(
  request: RequestMutateType<TVariables>,
  options?: {
    invalidate: InvalidateQueryFilters['queryKey'] | InvalidateQueryFilters['queryKey'][]
  } & UseMutationOptions<TData, TError, TVariables, TOnMutateResult>,
): UseMutationResult<TData, TError, TVariables, TOnMutateResult> {
  const baseURL =
    process.env.NODE_ENV === 'development' ? 'http://localhost:6065/api/' : 'https://actions.medsystem.co/api/'
  const newOptions: UseMutationOptions<TData, TError, TVariables, TOnMutateResult> = {
    ...options,
    mutationKey: options?.mutationKey,
    onSuccess: async (data, variables, onMutateResult, context) => {
      let queryKeyList: InvalidateQueryFilters['queryKey'][] = []

      if (Array.isArray(options?.invalidate)) {
        if (Array.isArray(options.invalidate[0])) {
          queryKeyList = options.invalidate as InvalidateQueryFilters['queryKey'][]
        } else {
          queryKeyList.push(options.invalidate)
        }
      }

      if (queryKeyList.length > 0) {
        await Promise.all(queryKeyList.map((queryKey) => queryClient.invalidateQueries({ queryKey })) || [])
      }
      if (options?.onSuccess) {
        await options.onSuccess(data, variables, onMutateResult, context)
      }
    },
    retry: 0,
  }

  newOptions.mutationFn = async (mutationData: TVariables) => {
    const requestObj = typeof request === 'function' ? request(mutationData) : request

    const mergedRequest = {
      formEncode: typeof requestObj !== 'string' && 'formEncode' in requestObj ? requestObj.formEncode : false,
      headers: {
        ...(typeof requestObj !== 'string' && 'headers' in requestObj ? requestObj.headers : {}),
      },
      method: typeof requestObj === 'string' ? 'post' : requestObj.method,
      url: typeof requestObj === 'string' ? requestObj : requestObj.url,
    }

    if (mergedRequest.method === 'put') {
      const response = await axiosPrivate.put<TData>(mergedRequest.url, mutationData, {
        baseURL,
        headers: {
          ...mergedRequest.headers,
          'Content-Type': mergedRequest.formEncode ? 'application/x-www-form-urlencoded' : 'application/json',
        },
      })
      return response.data
    }
    if (mergedRequest.method === 'delete') {
      const response = await axiosPrivate.delete<TData>(mergedRequest.url, {
        baseURL,
        headers: {
          ...mergedRequest.headers,
          'Content-Type': 'application/json',
        },
      })
      return response.data
    }

    const response = await axiosPrivate.post<TData>(mergedRequest.url, mutationData, {
      baseURL,
      headers: {
        ...mergedRequest.headers,
        'Content-Type': mergedRequest.formEncode ? 'application/x-www-form-urlencoded' : 'application/json',
      },
    })
    return response.data
  }

  return useMutation<TData, TError, TVariables, TOnMutateResult>(newOptions, queryClient)
}
